import React, { useEffect, useState } from 'react';
import {
  ErrorOutline,
  ExpandMore,
  ExpandLess,
  Refresh,
  Schedule,
  AccessTime,
  TrendingUpOutlined,
  PieChartOutlined,
  NotificationsActiveOutlined,
  ShowChartOutlined,
  CheckCircleOutline,
  SyncAlt,
  WarningAmber,
  RemoveCircleOutline,
  PlayArrow,
} from '@mui/icons-material';
import { getCronStatus, CronTask, CronRun, canTrigger, triggerCronTask, getPipelineRuns, PipelineRun, triggerPipelineRun, getAccountOptions, AccountOption } from '../../services/cronStatusService';

// ─── Color palettes (inline styles — custom Tailwind palette lacks standard colors) ───

const COLORS = {
  success: { bg: '#f0fdf4', border: '#86efac', text: '#15803d', iconColor: '#22c55e' },
  running: { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8', iconColor: '#3b82f6' },
  failed:  { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c', iconColor: '#ef4444' },
  stale:   { bg: '#fffbeb', border: '#fcd34d', text: '#a16207', iconColor: '#f59e0b' },
  none:    { bg: '#f9fafb', border: '#d1d5db', text: '#6b7280', iconColor: '#9ca3af' },
};

// ─── Helpers ──────────────────────────────────────────────────────

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const formatRelative = (dateStr: string | null): string => {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const formatDateShort = (value: string | null): string => {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return value;
  }
};

const formatTriggeredBy = (value: string | null): string => {
  if (!value) return 'System';
  if (value === 'cloud_function') return 'Scheduled';
  if (value === 'scheduler:cron') return 'Scheduled';
  if (value.startsWith('admin:')) return 'Manual';
  return value;
};

// ─── Status logic ────────────────────────────────────────────────

type StatusKey = 'success' | 'running' | 'failed' | 'stale' | 'none';

const getStatusKey = (task: CronTask): StatusKey => {
  const latest = task.latest;
  if (!latest) return 'none';
  if (latest.status === 'running') return 'running';
  if (latest.status === 'failed') return 'failed';
  const isMonthly = task.key === 'volatility_calc';
  const staleMs = isMonthly ? 35 * 24 * 3600000 : 26 * 3600000;
  const age = latest.startedAt ? Date.now() - new Date(latest.startedAt).getTime() : Infinity;
  if (age > staleMs) return 'stale';
  return 'success';
};

const STATUS_LABELS: Record<StatusKey, string> = {
  success: 'OK', running: 'Running', failed: 'Failed', stale: 'Stale', none: 'No runs',
};

const getStatusIcon = (key: StatusKey, size = 16) => {
  const c = COLORS[key];
  switch (key) {
    case 'success': return <CheckCircleOutline sx={{ fontSize: size, color: c.iconColor }} />;
    case 'running': return <SyncAlt className="animate-spin" sx={{ fontSize: size, color: c.iconColor }} />;
    case 'failed': return <ErrorOutline sx={{ fontSize: size, color: c.iconColor }} />;
    case 'stale': return <WarningAmber sx={{ fontSize: size, color: c.iconColor }} />;
    case 'none': return <RemoveCircleOutline sx={{ fontSize: size, color: c.iconColor }} />;
  }
};

// Task-specific summary line
const getSummaryLine = (task: CronTask): string => {
  const s = task.latest?.summary;
  if (!s) return '';
  switch (task.key) {
    case 'price_fetch':
      return `${s.success ?? 0} instruments, ${s.pricePointsSaved?.toLocaleString() ?? 0} points saved`;
    case 'exposure_calc':
      return `${s.accounts ?? 0} accounts, ${s.totalRows?.toLocaleString() ?? 0} rows`;
    case 'strategy_eval':
      return `${s.accounts ?? 0} accounts, ${s.totalNotifications ?? 0} notifications`;
    case 'volatility_calc':
      return `${s.calculated ?? 0}/${s.total ?? 0} formulas calculated`;
    default:
      return JSON.stringify(s);
  }
};

// Icon per task
const TASK_ICONS: Record<string, React.ReactNode> = {
  price_fetch: <TrendingUpOutlined sx={{ fontSize: 18 }} />,
  exposure_calc: <PieChartOutlined sx={{ fontSize: 18 }} />,
  strategy_eval: <NotificationsActiveOutlined sx={{ fontSize: 18 }} />,
  volatility_calc: <ShowChartOutlined sx={{ fontSize: 18 }} />,
};

// ─── Status badge (inline-styled) ────────────────────────────────

const getRunStatusKey = (status: string): StatusKey => {
  switch (status) {
    case 'running': return 'running';
    case 'success': return 'success';
    case 'failed': return 'failed';
    case 'partial': return 'stale';
    default: return 'none';
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const c = COLORS[getRunStatusKey(status)];
  return (
    <span
      style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
      className="inline-block px-2 py-0.5 rounded text-[10px] font-medium"
    >
      {status}
    </span>
  );
};

// ─── Expandable history ───────────────────────────────────────────

const RunHistory: React.FC<{ runs: CronRun[]; taskKey: string }> = ({ runs, taskKey }) => {
  if (runs.length <= 1) {
    return <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>No previous runs</p>;
  }

  return (
    <div className="mt-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <th className="pb-1.5 font-medium text-[10px] uppercase tracking-wider" style={{ color: '#6b7280' }}>Time</th>
            <th className="pb-1.5 font-medium text-[10px] uppercase tracking-wider" style={{ color: '#6b7280' }}>Status</th>
            <th className="pb-1.5 font-medium text-[10px] uppercase tracking-wider" style={{ color: '#6b7280' }}>Duration</th>
            <th className="pb-1.5 font-medium text-[10px] uppercase tracking-wider" style={{ color: '#6b7280' }}>Trigger</th>
            <th className="pb-1.5 font-medium text-[10px] uppercase tracking-wider" style={{ color: '#6b7280' }}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {runs.slice(0, 10).map((run) => {
            const rowColor = COLORS[getRunStatusKey(run.status)];
            return (
              <tr key={run.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td className="py-1.5" style={{ color: '#4b5563' }}>{formatDateShort(run.startedAt)}</td>
                <td className="py-1.5"><StatusBadge status={run.status} /></td>
                <td className="py-1.5" style={{ color: '#6b7280' }}>{formatDuration(run.durationMs)}</td>
                <td className="py-1.5" style={{ color: '#6b7280' }}>{formatTriggeredBy(run.triggeredBy)}</td>
                <td className="py-1.5 truncate max-w-[200px]">
                  {run.error
                    ? <span style={{ color: rowColor.text }} title={run.error}>{run.error}</span>
                    : <span style={{ color: '#6b7280' }}>{getRunSummaryShort(run, taskKey)}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const getRunSummaryShort = (run: CronRun, taskKey: string): string => {
  const s = run.summary;
  if (!s) return '';
  switch (taskKey) {
    case 'price_fetch': return `${s.success ?? 0} ok, ${s.failed ?? 0} fail`;
    case 'exposure_calc': return `${s.totalRows?.toLocaleString() ?? 0} rows`;
    case 'strategy_eval': return `${s.totalNotifications ?? 0} notif`;
    case 'volatility_calc': return `${s.calculated ?? 0}/${s.total ?? 0}`;
    default: return '';
  }
};

// ─── Task Card ────────────────────────────────────────────────────

const TaskCard: React.FC<{
  task: CronTask;
  triggering: boolean;
  onTrigger: (key: string) => void;
}> = ({ task, triggering, onTrigger }) => {
  const [expanded, setExpanded] = useState(false);
  const statusKey = getStatusKey(task);
  const colors = COLORS[statusKey];
  const icon = TASK_ICONS[task.key] || <Schedule sx={{ fontSize: 18 }} />;
  const latest = task.latest;
  const showTrigger = canTrigger(task.key);

  return (
    <div className="bg-white rounded-lg overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
      {/* Colored status header */}
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: colors.bg }}>
        <div className="flex items-center gap-2">
          <span style={{ color: '#6b7280' }}>{icon}</span>
          <h3 className="text-sm font-semibold" style={{ color: '#111827' }}>{task.label}</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {getStatusIcon(statusKey)}
          <span className="text-xs font-medium" style={{ color: colors.text }}>{STATUS_LABELS[statusKey]}</span>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-1 text-xs mb-2" style={{ color: '#9ca3af' }}>
          <Schedule sx={{ fontSize: 13 }} />
          <span>{task.schedule}</span>
        </div>

        {latest ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1" style={{ color: '#4b5563' }}>
                <AccessTime sx={{ fontSize: 13 }} />
                {formatRelative(latest.startedAt)}
              </span>
              {latest.durationMs != null && (
                <span style={{ color: '#9ca3af' }}>{formatDuration(latest.durationMs)}</span>
              )}
              <StatusBadge status={latest.status} />
            </div>
          </div>
        ) : null}

        {latest && latest.status !== 'failed' && getSummaryLine(task) && (
          <p className="text-xs mt-1.5" style={{ color: '#6b7280' }}>{getSummaryLine(task)}</p>
        )}

        {latest?.error && (
          <p className="text-xs mt-1.5 truncate" style={{ color: colors.text }} title={latest.error}>
            {latest.error}
          </p>
        )}

        {!latest && (
          <p className="text-xs" style={{ color: '#9ca3af' }}>No runs recorded yet</p>
        )}

        {/* Trigger button */}
        {showTrigger && (
          <div className="mt-3">
            <button
              onClick={() => onTrigger(task.key)}
              disabled={triggering || latest?.status === 'running'}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded font-medium transition-colors disabled:opacity-50"
              style={{
                background: triggering ? '#e5e7eb' : '#3B82F6',
                color: triggering ? '#6b7280' : '#ffffff',
              }}
            >
              {triggering
                ? <><Refresh className="animate-spin" sx={{ fontSize: 14 }} /> Running...</>
                : <><PlayArrow sx={{ fontSize: 14 }} /> Run Now</>
              }
            </button>
          </div>
        )}
      </div>

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2 flex items-center justify-center gap-1 text-xs transition-colors"
        style={{ borderTop: '1px solid #f3f4f6', color: '#9ca3af' }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f9fafb')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        {expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
        {expanded ? 'Hide history' : `History (${task.runs.length} runs)`}
      </button>

      {expanded && (
        <div className="px-4 pb-3" style={{ borderTop: '1px solid #f3f4f6' }}>
          <RunHistory runs={task.runs} taskKey={task.key} />
        </div>
      )}
    </div>
  );
};

// ─── Step indicator dot ─────────────────────────────────────────

const StepDot: React.FC<{ ok: boolean | null; label: string }> = ({ ok, label }) => {
  const color = ok === null ? '#d1d5db' : ok ? '#22c55e' : '#ef4444';
  return (
    <span title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#6b7280' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
      {label}
    </span>
  );
};

const TRIGGER_LABELS: Record<string, string> = {
  batch_approved: 'Batch',
  price_fetch: 'Prices',
  config_saved: 'Config',
  cron: 'Cron',
  manual: 'Manual',
};

// ─── Pipeline Runs Table ─────────────────────────────────────────

const PipelineRunRow: React.FC<{ run: PipelineRun }> = ({ run }) => {
  const [expanded, setExpanded] = useState(false);
  const hasLog = run.log && run.log.length > 0;
  return (
    <>
      <tr
        style={{ borderBottom: expanded ? 'none' : '1px solid #f3f4f6', cursor: hasLog ? 'pointer' : 'default' }}
        onClick={() => hasLog && setExpanded(e => !e)}
        onMouseEnter={e => { if (hasLog) (e.currentTarget as HTMLTableRowElement).style.background = '#f9fafb'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
      >
        <td className="py-1.5 pr-3 whitespace-nowrap" style={{ color: '#4b5563' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {hasLog && (expanded ? <ExpandLess sx={{ fontSize: 13, color: '#9ca3af' }} /> : <ExpandMore sx={{ fontSize: 13, color: '#9ca3af' }} />)}
            {formatDateShort(run.startedAt)}
          </span>
        </td>
        <td className="py-1.5 pr-3" style={{ color: '#111827', fontWeight: 500 }}>{run.accountName ?? run.accountId}</td>
        <td className="py-1.5 pr-3">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#eff6ff', color: '#1d4ed8' }}>
            {TRIGGER_LABELS[run.trigger] ?? run.trigger}
          </span>
        </td>
        <td className="py-1.5 pr-3">
          <span style={{ display: 'flex', gap: 6 }}>
            <StepDot ok={run.steps.exposure} label="Exp" />
            <StepDot ok={run.steps.derivation} label="Deriv" />
            <StepDot ok={run.steps.eval} label="Eval" />
          </span>
        </td>
        <td className="py-1.5 pr-3" style={{ color: run.notificationsCreated ? '#15803d' : '#9ca3af' }}>
          {run.notificationsCreated ?? '—'}
        </td>
        <td className="py-1.5 pr-3" style={{ color: '#6b7280' }}>{formatDuration(run.durationMs)}</td>
        <td className="py-1.5 max-w-[220px] truncate" style={{ color: '#b91c1c' }} title={run.error ?? ''}>
          {run.error ?? ''}
        </td>
      </tr>
      {expanded && hasLog && (
        <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
          <td colSpan={7} className="px-6 pb-2 pt-1">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {run.log.map((entry, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.ok ? '#22c55e' : '#ef4444', flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ color: '#374151', fontWeight: 500, minWidth: 70 }}>{entry.step}</span>
                  <span style={{ color: '#6b7280' }}>{entry.detail}</span>
                  <span style={{ color: '#9ca3af', marginLeft: 'auto' }}>{formatDuration(entry.durationMs)}</span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const PipelineRunsTable: React.FC<{ runs: PipelineRun[] }> = ({ runs }) => {
  if (runs.length === 0) {
    return <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>No pipeline runs yet.</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
            {['Time', 'Account', 'Trigger', 'Steps', 'Notifs', 'Duration', 'Error'].map(h => (
              <th key={h} className="pb-1.5 text-left font-medium text-[10px] uppercase tracking-wider pr-3" style={{ color: '#6b7280' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map(run => <PipelineRunRow key={run.id} run={run} />)}
        </tbody>
      </table>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────

export const CronJobs: React.FC = () => {
  const [tasks, setTasks] = useState<CronTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [triggeringTask, setTriggeringTask] = useState<string | null>(null);
  const [pipelineRuns, setPipelineRuns] = useState<PipelineRun[]>([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(undefined);
  const [triggeringPipeline, setTriggeringPipeline] = useState(false);
  const [pipelineMessage, setPipelineMessage] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCronStatus();
      setTasks(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cron status');
    } finally {
      setLoading(false);
    }
  };

  const fetchPipelineRuns = async (accountId?: number) => {
    try {
      setPipelineLoading(true);
      const runs = await getPipelineRuns(accountId, 50);
      setPipelineRuns(runs);
    } catch {
      // non-fatal
    } finally {
      setPipelineLoading(false);
    }
  };

  const handleAccountChange = (id: number | undefined) => {
    setSelectedAccountId(id);
    fetchPipelineRuns(id);
  };

  const handleRunPipeline = async () => {
    if (!selectedAccountId) return;
    const accountName = accounts.find(a => a.id === selectedAccountId)?.name ?? `account #${selectedAccountId}`;
    if (!confirm(`Trigger full pipeline for "${accountName}"?`)) return;
    setTriggeringPipeline(true);
    setPipelineMessage(null);
    try {
      const result = await triggerPipelineRun(selectedAccountId);
      setPipelineMessage(result.message);
      setTimeout(() => fetchPipelineRuns(selectedAccountId), 1500);
    } catch (err) {
      setPipelineMessage(err instanceof Error ? err.message : 'Failed');
    } finally {
      setTriggeringPipeline(false);
    }
  };

  const handleTrigger = async (taskKey: string) => {
    if (!confirm(`Run ${taskKey.replace('_', ' ')} now?`)) return;
    setTriggeringTask(taskKey);
    setError(null);
    try {
      await triggerCronTask(taskKey);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to trigger ${taskKey}`);
    } finally {
      setTriggeringTask(null);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPipelineRuns();
    getAccountOptions().then(setAccounts).catch(() => {});
    const interval = setInterval(() => { fetchData(); fetchPipelineRuns(selectedAccountId); }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="px-4 py-3">
      {/* Page header */}
      <div className="flex justify-between items-center mb-3">
        <div>
          <h1 className="text-xl font-bold text-left" style={{ color: '#111827' }}>Cron Jobs</h1>
          <p className="text-xs mt-0.5 text-left" style={{ color: '#6b7280' }}>
            System task status & run history
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: '#9ca3af' }}>
            Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-1.5 rounded transition-colors disabled:opacity-50"
            style={{ color: '#6b7280' }}
            title="Refresh"
          >
            <Refresh className={loading ? 'animate-spin' : ''} sx={{ fontSize: 20 }} />
          </button>
        </div>
      </div>

      {/* Summary strip */}
      {tasks.length > 0 && (
        <div
          className="flex items-center gap-4 mb-3 px-3 py-2 rounded text-xs"
          style={{ background: '#F1F4F8', border: '1px solid #E6E9EE' }}
        >
          <span style={{ color: '#6b7280' }}>{tasks.length} tasks</span>
          {tasks.map((t) => {
            const sk = getStatusKey(t);
            const c = COLORS[sk];
            return (
              <span key={t.key} className="flex items-center gap-1.5">
                {getStatusIcon(sk, 14)}
                <strong style={{ color: '#374151' }}>{t.label.split('(')[0].trim()}</strong>
                <span style={{ color: c.text }}>{STATUS_LABELS[sk]}</span>
              </span>
            );
          })}
          <span className="ml-auto" style={{ color: '#9ca3af' }}>Auto-refresh every 60s</span>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div
          className="mb-3 p-2 rounded text-xs flex items-center gap-2"
          style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5' }}
        >
          <ErrorOutline sx={{ fontSize: 16 }} />
          {error}
        </div>
      )}

      {/* Task cards grid */}
      {loading && tasks.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-xs" style={{ color: '#6b7280' }}>Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tasks.map((task) => (
            <TaskCard
              key={task.key}
              task={task}
              triggering={triggeringTask === task.key}
              onTrigger={handleTrigger}
            />
          ))}
        </div>
      )}

      {/* Pipeline Runs */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-base font-semibold" style={{ color: '#111827' }}>Pipeline Runs</h2>
            <p className="text-xs" style={{ color: '#6b7280' }}>Reactive calculations triggered by data changes</p>
          </div>
          <button
            onClick={() => fetchPipelineRuns(selectedAccountId)}
            disabled={pipelineLoading}
            className="p-1.5 rounded transition-colors disabled:opacity-50"
            style={{ color: '#6b7280' }}
            title="Refresh"
          >
            <Refresh className={pipelineLoading ? 'animate-spin' : ''} sx={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Controls row: account selector + run button */}
        <div className="flex items-center gap-2 mb-2">
          <select
            value={selectedAccountId ?? ''}
            onChange={e => handleAccountChange(e.target.value ? Number(e.target.value) : undefined)}
            className="text-xs rounded px-2 py-1.5 border"
            style={{ borderColor: '#d1d5db', color: '#374151', minWidth: 180 }}
          >
            <option value="">All accounts</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>

          <button
            onClick={handleRunPipeline}
            disabled={!selectedAccountId || triggeringPipeline}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded font-medium transition-colors disabled:opacity-40"
            style={{ background: '#3b82f6', color: '#fff' }}
          >
            {triggeringPipeline
              ? <><Refresh className="animate-spin" sx={{ fontSize: 13 }} /> Running...</>
              : <><PlayArrow sx={{ fontSize: 13 }} /> Run Now</>
            }
          </button>

          {pipelineMessage && (
            <span className="text-xs" style={{ color: '#15803d' }}>{pipelineMessage}</span>
          )}
        </div>

        <div className="flex gap-3 mb-2 flex-wrap" style={{ fontSize: 10, color: '#9ca3af' }}>
          <span><span style={{ fontWeight: 600, color: '#6b7280' }}>Exp</span> — Exposure snapshot</span>
          <span><span style={{ fontWeight: 600, color: '#6b7280' }}>Deriv</span> — Hedge derivation cache</span>
          <span><span style={{ fontWeight: 600, color: '#6b7280' }}>Eval</span> — Watch rule evaluation</span>
          <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {[['#d1d5db', 'skipped'], ['#22c55e', 'ok'], ['#ef4444', 'error']].map(([color, label]) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                {label}
              </span>
            ))}
          </span>
        </div>

        <div
          className="bg-white rounded-lg px-4 py-3"
          style={{ border: '1px solid #e5e7eb' }}
        >
          {pipelineLoading && pipelineRuns.length === 0 ? (
            <p className="text-xs" style={{ color: '#9ca3af' }}>Loading...</p>
          ) : (
            <PipelineRunsTable runs={pipelineRuns} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CronJobs;
